import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/joy";
import {
  ArrowsClockwise as RenderIcon,
  FilePdf as ProofIcon,
  PaperPlaneTilt as SubmitIcon,
  X as CancelIcon,
} from "@phosphor-icons/react";
import { format, parseISO } from "date-fns";
import { formatMoney } from "../../lib/directMailApi";
import {
  MAIL_BATCH_STATUS_LABELS,
  MAIL_TEMPLATE_LABELS,
  type MailBatch,
  type MailBatchStatus,
  type MailTemplateKind,
} from "../../types/directMail";

/**
 * The batch serializer exposes the proof as `proof_pdf_url` and adds the
 * campaign's `template_kind`; neither is on the shared MailBatch shape.
 */
export type MailBatchRow = MailBatch & {
  proof_pdf_url?: string | null;
  template_kind?: MailTemplateKind | null;
};

const STATUS_COLORS: Record<
  MailBatchStatus,
  "primary" | "neutral" | "success" | "warning" | "danger"
> = {
  draft: "neutral",
  submitted: "primary",
  printing: "warning",
  mailed: "success",
  failed: "danger",
  cancelled: "neutral",
};

function formatDay(value: string | null | undefined): string {
  if (!value) return "—";
  try {
    return format(parseISO(value), "MMM d, yyyy");
  } catch {
    return "—";
  }
}

const Fact: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box>
    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
      {label}
    </Typography>
    <Typography level="body-sm">{value}</Typography>
  </Box>
);

export interface BatchCardProps {
  batch: MailBatchRow;
  isOrgAdmin: boolean;
  /** True while a queued proof render is being polled for. */
  rendering: boolean;
  busy: boolean;
  onRenderProof: (batch: MailBatchRow) => void;
  onSubmit: (batch: MailBatchRow) => void;
  onCancel: (batch: MailBatchRow) => void;
  onWorkspace?: (batch: MailBatchRow) => void;
}

export const BatchCard: React.FC<BatchCardProps> = ({
  batch,
  isOrgAdmin,
  rendering,
  busy,
  onRenderProof,
  onSubmit,
  onCancel,
  onWorkspace,
}) => {
  const isDraft = batch.status === "draft";
  const isFailed = batch.status === "failed";
  const canRender = isDraft && !batch.paid_at;
  const canSubmit = isDraft || isFailed;
  const proofUrl = batch.proof_pdf_url ?? batch.proof_pdf ?? null;

  return (
    <Card variant="outlined" sx={{ p: 2, gap: 1.5 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ sm: "flex-start" }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Typography level="title-md">
              {batch.campaign_name || "Untitled campaign"}
            </Typography>
            <Chip size="sm" variant="soft" color={STATUS_COLORS[batch.status]}>
              {MAIL_BATCH_STATUS_LABELS[batch.status]}
            </Chip>
            {batch.template_kind && (
              <Chip size="sm" variant="outlined">
                {MAIL_TEMPLATE_LABELS[batch.template_kind]}
              </Chip>
            )}
          </Stack>
          <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.25 }}>
            {batch.reference} · created {formatDay(batch.created_at)}
            {batch.created_by_name ? ` by ${batch.created_by_name}` : ""}
          </Typography>
        </Box>
        <Box sx={{ textAlign: { sm: "right" }, flexShrink: 0 }}>
          <Typography level="h4">{formatMoney(batch.total_amount)}</Typography>
          <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
            {batch.piece_count} × {formatMoney(batch.unit_price)}
          </Typography>
        </Box>
      </Stack>

      {batch.error_message && (
        <Alert color="danger" variant="soft" size="sm">
          {batch.error_message}
        </Alert>
      )}

      <Divider />

      <Grid container spacing={1.5}>
        <Grid xs={6} sm={3}>
          <Fact label="Pieces" value={batch.piece_count} />
        </Grid>
        <Grid xs={6} sm={3}>
          <Fact
            label="Charged"
            value={batch.paid_at ? formatDay(batch.paid_at) : "Not yet"}
          />
        </Grid>
        <Grid xs={6} sm={3}>
          <Fact label="Submitted" value={formatDay(batch.submitted_at)} />
        </Grid>
        <Grid xs={6} sm={3}>
          <Fact
            label="Expected delivery"
            value={formatDay(batch.expected_delivery_date)}
          />
        </Grid>
      </Grid>

      <Divider />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {canRender && (
          <Button
            size="sm"
            variant="outlined"
            color="neutral"
            startDecorator={
              rendering ? <CircularProgress size="sm" /> : <RenderIcon />
            }
            disabled={busy || rendering || batch.piece_count === 0}
            onClick={() => onRenderProof(batch)}
          >
            {rendering
              ? "Rendering…"
              : proofUrl
                ? "Re-render proof"
                : "Render proof"}
          </Button>
        )}
        {proofUrl && (
          <Button
            size="sm"
            variant="soft"
            component="a"
            href={proofUrl}
            target="_blank"
            rel="noopener noreferrer"
            startDecorator={<ProofIcon />}
          >
            Open proof PDF
          </Button>
        )}
        {onWorkspace && (
          <Button size="sm" variant="soft" onClick={() => onWorkspace(batch)}>
            Review proof & fulfillment
          </Button>
        )}
        {canSubmit && isOrgAdmin && (
          <Button
            size="sm"
            startDecorator={<SubmitIcon />}
            disabled={
              busy ||
              !proofUrl ||
              !batch.proof_approved_at ||
              batch.piece_count === 0
            }
            onClick={() => onSubmit(batch)}
          >
            Submit &amp; mail
          </Button>
        )}
        {/* A failed batch can be cancelled too, and that matters more than the
            draft case: its pins are stuck at "batched", and a pinned house owns
            its address, so until the batch is torn down those houses cannot be
            re-pinned or mailed under any other campaign. */}
        {canSubmit && (
          <Button
            size="sm"
            variant="plain"
            color="danger"
            startDecorator={<CancelIcon />}
            disabled={busy}
            onClick={() => onCancel(batch)}
          >
            {isDraft ? "Cancel draft" : "Cancel batch"}
          </Button>
        )}
      </Stack>

      {canRender && !proofUrl && (
        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
          Render the proof and read it before submitting — it is the last look
          you get at what lands in someone's mailbox.
        </Typography>
      )}
      {canRender && proofUrl && !isOrgAdmin && (
        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
          Submitting charges the card on file, so an organization admin has to
          do it.
        </Typography>
      )}
    </Card>
  );
};
